/**
 * Named commands: the things a keyboard shortcut (or a menu, later) can trigger, such as
 * `graph.undo` or `window.toggle presets`.
 *
 * Nobody owns the full list. Each service contributes its own commands next to its own code
 * (`graphCommands` in `client/actions.ts`, `projectCommands` in `state/project.ts`, and so on)
 * and each subsystem hands its set over (`commands` in `app/subsystems.ts`), added here by
 * `app/services.ts`. Removing a feature removes its commands with it, and
 * adding one never touches `keys.ts`.
 *
 * The names are the ones the config's `keybinds` section uses (`config/default.kdl`).
 */

/** Runs a command. `argument` is the bind's optional third value (`null` when absent). */
export type Command = (argument: string | null) => void;

/** A set of commands contributed by one service: name → command. */
export type CommandSet = Record<string, Command>;

/** Finds a command that is not in a fixed set (plugins come and go): `undefined` if unknown. */
export type CommandResolver = (name: string) => Command | undefined;

/** The registry. */
export interface Commands {
  /** Adds a service's commands. A name added twice keeps the last one (and says so). */
  add(set: CommandSet): void;
  /** Adds a resolver, asked for names no set has (plugin commands, `plugin:<id>:<command>`). */
  addResolver(resolver: CommandResolver): void;
  /** Runs a command. Returns `false` when no command has that name (nothing happens). */
  run(name: string, argument?: string | null): boolean;
  /** Every name of the fixed sets, sorted (for help screens and tests). */
  names(): string[];
}

/** Creates an empty registry. */
export function createCommands(): Commands {
  const table = new Map<string, Command>();
  const resolvers: CommandResolver[] = [];
  return {
    add(set) {
      for (const [name, command] of Object.entries(set)) {
        if (table.has(name)) console.warn(`command ${name} registered twice; the last one wins`);
        table.set(name, command);
      }
    },
    addResolver(resolver) {
      resolvers.push(resolver);
    },
    run(name, argument = null) {
      const command = table.get(name) ?? resolvers.map((r) => r(name)).find((c) => c !== undefined);
      if (!command) return false;
      command(argument);
      return true;
    },
    names: () => [...table.keys()].sort(),
  };
}
